import { isAuthError } from "@supabase/supabase-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  resetAuthPassword,
  signIn,
  signInSSO,
  signOut,
  signUp,
  updateAuthUser,
  updateAuthUserProfile,
  type ResetPasswordProps,
  type SignUpProps,
} from "@/api/resources/auth/api";
import { authQueries, authQueryKeys } from "@/api/resources/auth/queries";
import { usersQueryKeys } from "@/api/resources/users/queries";
import { useNotifications } from "@/hooks/useNotifications";
import { useWhitelabelBrand } from "@/hooks/useWhitelabelBrand";
import { getAuthErrorMessage } from "@/utils/authErrorMessage";
import { trackPosthogEvent } from "@/utils/posthog";

const useAuthSession = () => {
  return useQuery(authQueries.getAuthSession());
};

export const useAuthClaims = () => {
  return useQuery(authQueries.getAuthClaims());
};

const useAuthUser = () => {
  return useQuery(authQueries.getAuthUser());
};

const useAuthUserProfile = (id?: string) => {
  const { data: claims } = useAuthClaims();
  return useQuery(authQueries.getAuthUserProfile(id ?? claims?.sub ?? ""));
};

const useAuthUserPermissions = (id?: string) => {
  const { data: claims } = useAuthClaims();
  return useQuery(authQueries.getAuthUserPermissions(id ?? claims?.sub ?? ""));
};

const useSignIn = () => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signIn,
    onMutate: () => {
      // re-enable queries, which sign-out disables globally (see providers/Auth.tsx)
      queryClient.setDefaultOptions({
        queries: { ...queryClient.getDefaultOptions().queries, enabled: undefined },
      });
      // remove all potential left-over queries in react-query cache, to mitigate conflicts and trigger re-evaluation of ALL app data
      queryClient.removeQueries();
    },
    onError: (_error, _variables, _context) => {
      notify.error(t("notify.login.error"));
    },
    onSuccess: () => {
      notify.success(t("notify.login.success"));
      trackPosthogEvent(`signIn`, `user`);
    },
  });
};

const useSignInSSO = () => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signInSSO,
    onMutate: () => {
      // re-enable queries, which sign-out disables globally (see providers/Auth.tsx)
      queryClient.setDefaultOptions({
        queries: { ...queryClient.getDefaultOptions().queries, enabled: undefined },
      });
      // remove all potential left-over queries in react-query cache, to mitigate conflicts and trigger re-evaluation of ALL app data
      queryClient.removeQueries();
    },
    onError: (_error, _variables, _context) => {
      notify.error(t("notify.login.error"));
    },
    onSuccess: () => {
      notify.success(t("notify.login.success"));
      trackPosthogEvent(`signInSSO`, `user`);
    },
  });
};

const useSignUp = () => {
  const { t, i18n } = useTranslation();
  const notify = useNotifications();
  const { whitelabelBrand, appBaseUrl } = useWhitelabelBrand();

  return useMutation({
    mutationFn: (values: Pick<SignUpProps, "email" | "password">) => {
      return signUp({
        ...values,
        language: i18n.resolvedLanguage,
        whitelabelBrandKey: whitelabelBrand.key,
        appBaseUrl,
      });
    },
    onError: () => {
      notify.error(t("notify.register.error"));
    },
    onSuccess: () => {
      notify.success(t("notify.register.success"));
      trackPosthogEvent(`signUp`, `user`);
    },
  });
};

const useSignOut = () => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: signOut,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: authQueryKeys.all() });
      await queryClient.cancelQueries({ queryKey: MASTER_INVALIDATION_KEY });
    },
    onError: () => {
      notify.error(t("notify.logout.error"));
    },
  });
};

const useResetAuthPassword = () => {
  const { t, i18n } = useTranslation();
  const notify = useNotifications();
  const { whitelabelBrand, appBaseUrl } = useWhitelabelBrand();

  return useMutation({
    mutationFn: (values: Pick<ResetPasswordProps, "email">) => {
      return resetAuthPassword({
        ...values,
        language: i18n.resolvedLanguage,
        whitelabelBrandKey: whitelabelBrand.key,
        appBaseUrl,
      });
    },
    onError: () => {
      notify.error(t("notify.forgot-password.error"));
    },
    onSuccess: () => {
      notify.success(t("notify.forgot-password.success"));
      trackPosthogEvent(`resetPassword`, `user`);
    },
  });
};

const useUpdateAuthUser = (id?: string) => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAuthUser,
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.cancelQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
    },
    onError: (error) => {
      if (isAuthError(error)) {
        notify.error(getAuthErrorMessage(error));
      } else {
        notify.error(t("notify.update.error", { e: t("user") }));
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.invalidateQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
      notify.success(t("notify.update.success", { e: t("user") }));
      trackPosthogEvent(`update`, `user`);
    },
  });
};

const useUpdateAuthUserProfile = (id?: string) => {
  const { t } = useTranslation();
  const notify = useNotifications();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAuthUserProfile,
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.cancelQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
    },
    onError: () => {
      notify.error(t("notify.update.error", { e: t("user") }));
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [...authQueryKeys.all()] }),
        queryClient.invalidateQueries({ queryKey: [...usersQueryKeys.byId(id)] }),
      ]);
      notify.success(t("notify.update.success", { e: t("user") }));
      trackPosthogEvent(`update`, `user`);
    },
  });
};

export const useAuth = () => {
  // we're using the sync mutate functions, since they don't need to be wrapped & handled via try-catch
  // otherwise try-catch would be mandatory and all error handling of react-query (see sources of used hooks) would be useless
  const { data: session, isLoading: loadingSession } = useAuthSession();
  const { data: claims, isLoading: loadingClaims } = useAuthClaims();
  const { data: user, isLoading: loadingUser } = useAuthUser();
  const { data: profile, isLoading: loadingProfile } = useAuthUserProfile();
  const { data: permissions, isLoading: loadingPermissions } = useAuthUserPermissions();

  const { mutate: _signIn, isPending: loadingSignIn } = useSignIn();
  const { mutate: _signInSSO, isPending: loadingSignInSSO } = useSignInSSO();
  const { mutate: _signUp, isPending: loadingSignUp } = useSignUp();
  const { mutate: _signOut, isPending: loadingSignOut } = useSignOut();
  const { mutate: _resetAuthPassword, isPending: loadingResetAuthPassword } = useResetAuthPassword();
  const { mutate: _updateAuthUser, isPending: loadingUpdateAuthUser } = useUpdateAuthUser();
  const { mutate: _updateAuthUserProfile, isPending: loadingUpdateAuthUserProfile } = useUpdateAuthUserProfile();

  const loading = useMemo(
    () =>
      [
        loadingSession,
        loadingClaims,
        loadingUser,
        loadingProfile,
        loadingPermissions,
        loadingSignIn,
        loadingSignInSSO,
        loadingSignUp,
        loadingSignOut,
        loadingResetAuthPassword,
        loadingUpdateAuthUser,
        loadingUpdateAuthUserProfile,
      ].some((loadingState) => loadingState),
    [
      loadingSession,
      loadingClaims,
      loadingUser,
      loadingProfile,
      loadingPermissions,
      loadingResetAuthPassword,
      loadingSignIn,
      loadingSignInSSO,
      loadingSignOut,
      loadingSignUp,
      loadingUpdateAuthUser,
      loadingUpdateAuthUserProfile,
    ],
  );

  return {
    isAuthenticated: Boolean(claims),
    user_id: claims?.sub,
    session: session,
    claims: claims,
    loading: loading,
    user: user,
    profile: profile,
    permissions: permissions,
    signIn: _signIn,
    signInSSO: _signInSSO,
    signUp: _signUp,
    signOut: _signOut,
    resetAuthPassword: _resetAuthPassword,
    updateAuthUser: _updateAuthUser,
    updateAuthUserProfile: _updateAuthUserProfile,
    loadingSession,
    loadingClaims,
    loadingUser,
    loadingProfile,
    loadingPermissions,
    loadingSignIn,
    loadingSignInSSO,
    loadingSignUp,
    loadingSignOut,
    loadingResetAuthPassword,
    loadingUpdateAuthUser,
    loadingUpdateAuthUserProfile,
  };
};
